import type {
  BikeUpsertData,
  HelmetUpsertData,
  IntercomUpsertData,
  BikeServiceUpsertData
} from '~/types/bikes'
import { API } from '~/data/api'

export const useGarageService = () => {
  const interactionStore = useInteractionStore()
  const garageStore = useGarageStore()

  async function getBikeBrands() {
    const response = await API?.getBikeBrands()
    return response?.data
  }

  async function createHelmet(userId: number, data: HelmetUpsertData) {
    const response = await API?.createHelmet(userId, data)

    return response?.data
  }

  async function updateHelmet(helmetId: number, data: HelmetUpsertData) {
    const response = await API?.updateHelmet(helmetId, data)
    return response?.data
  }

  async function createIntercom(userId: number, data: IntercomUpsertData) {
    const response = await API?.createIntercom(userId, data)
    return response?.data
  }

  async function updateIntercom(intercomId: number, data: IntercomUpsertData) {
    const response = await API?.updateIntercom(intercomId, data)
    return response?.data
  }

  async function getBike(bikeID: number) {
    const response = await API?.getBike(bikeID)

    if (response?.data) {
      interactionStore.initItem('bikes', response.data.id, {
        likedByMe: response.data.liked_by_me,
        likeCount: response.data.likes_count,
        commentCount: response.data.comments_count
      })
    }

    return response?.data
  }

  async function createBike(userId: number, data: BikeUpsertData) {
    const response = await API?.createBike(userId, data)

    if (response?.data) {
      garageStore.addBike(response.data)
    }

    return response?.data
  }

  async function updateBike(bikeId: number, data: BikeUpsertData) {
    const response = await API?.updateBike(bikeId, data)

    if (response?.data) {
      garageStore.updateBike(response.data)
    }

    return response?.data
  }

  async function getHelmetBrands() {
    const response = await API?.getHelmetBrands()
    return response?.data
  }

  async function getIntercomBrands() {
    const response = await API?.getIntercomBrands()
    return response?.data
  }

  async function updateHelmetPhoto(helmetId: number, photo: File) {
    const response = await API?.updateHelmetPhoto(helmetId, photo)
    return response?.data
  }

  async function updateIntercomPhoto(intercomId: number, photo: File) {
    const response = await API?.updateIntercomPhoto(intercomId, photo)
    return response?.data
  }

  async function updateBikePhoto(bikeId: number, photo: File) {
    const response = await API?.updateBikePhoto(bikeId, photo)
    return response?.data
  }

  async function addAdditionalBikePhoto(bikeId: number, photo: File) {
    const response = await API?.addAdditionalBikePhoto(bikeId, photo)
    return response?.data
  }

  async function deleteAdditionalBikePhoto(photoId: number) {
    const response = await API?.deletePhoto(photoId)
    return response?.data
  }

  async function deleteBike(bikeId: number) {
    const response = await API?.deleteBike(bikeId)

    garageStore.removeBike(bikeId)

    return response?.data
  }

  async function deleteHelmet(helmetId: number) {
    const response = await API?.deleteHelmet(helmetId)
    return response?.data
  }

  async function deleteIntercom(intercomId: number) {
    const response = await API?.deleteIntercom(intercomId)
    return response?.data
  }

  async function getHelmet(helmetId: number) {
    const response = await API?.getHelmet(helmetId)

    if (response?.data) {
      interactionStore.initItem('helmets', response.data.id, {
        likedByMe: response.data.liked_by_me,
        likeCount: response.data.likes_count,
        commentCount: response.data.comments_count
      })
    }

    return response?.data
  }

  async function getIntercom(intercomId: number) {
    const response = await API?.getIntercom(intercomId)

    if (response?.data) {
      interactionStore.initItem('intercoms', response.data.id, {
        likedByMe: response.data.liked_by_me,
        likeCount: response.data.likes_count,
        commentCount: response.data.comments_count
      })
    }

    return response?.data
  }

  async function getBikeServices(bikeId: number) {
    const response = await API?.getBikeServices(bikeId)
    return response?.data
  }

  async function getBikeService(bikeId: number, serviceId: number) {
    const response = await API?.getBikeService(bikeId, serviceId)
    return response?.data
  }

  async function createBikeService(bikeId: number, data: BikeServiceUpsertData) {
    const response = await API?.createBikeService(bikeId, data)

    if (response?.data) {
      garageStore.updateBike(response.data.bike)
    }

    return response?.data
  }

  async function updateBikeService(bikeId: number, serviceId: number, data: BikeServiceUpsertData) {
    const response = await API?.updateBikeService(bikeId, serviceId, data)
    return response?.data
  }

  async function deleteBikeService(bikeId: number, serviceId: number) {
    const response = await API?.deleteBikeService(bikeId, serviceId)
    return response?.data
  }

  return {
    getBikeBrands,
    createHelmet,
    updateHelmet,
    createIntercom,
    updateIntercom,
    getBike,
    createBike,
    updateBike,
    getHelmetBrands,
    getIntercomBrands,
    updateHelmetPhoto,
    updateIntercomPhoto,
    updateBikePhoto,
    addAdditionalBikePhoto,
    deleteAdditionalBikePhoto,
    deleteBike,
    deleteHelmet,
    deleteIntercom,
    getHelmet,
    getIntercom,
    getBikeServices,
    getBikeService,
    createBikeService,
    updateBikeService,
    deleteBikeService
  }
}
